import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Save, Sparkles, Bot, Eye, Send, MessageCircle, Loader2, Paperclip, FileText, Files } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { settingsApi, documentsApi, welcomeApi } from "@/lib/crmApi";

const DEFAULT_TEMPLATE =
  "Bonjour {nom}, merci de votre intérêt pour SOGI SARL ! " +
  "Un conseiller vous recontacte rapidement avec toutes les informations utiles.";

/** Mirror of backend `_format_welcome_template`. Pure, no I/O — used only
 * for the live preview, the actual send still uses the backend formatter. */
function renderTemplate(tpl, nom) {
  const fullName = (nom || "").trim();
  const prenom = fullName ? fullName.split(/\s+/)[0] : "";
  try {
    return (tpl || "")
      .replace(/\{nom\}/g, fullName)
      .replace(/\{prenom\}/g, prenom);
  } catch {
    return tpl || "";
  }
}

// Maximum size for the admin's free-form AI instructions. Raised to give room
// for the conversational agent's detailed guidance (tone, rules, FAQ, project
// knowledge) — ~1000 words. Still bounded to keep the per-reply system prompt
// affordable.
const AI_INSTRUCTIONS_MAX = 6000;

/**
 * @param {Object} props
 * @param {string|null} [props.projetId] When set, the panel reads/writes
 *   per-project settings (overrides on top of global defaults). When null,
 *   it edits the GLOBAL singleton — the legacy behaviour.
 * @param {string} [props.projetNom] Display name shown in the "scope" hint.
 */
export default function WelcomeAutoSend({ projetId = null, projetNom = "" }) {
  const [enabled, setEnabled] = useState(false);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [smartReply, setSmartReply] = useState(false);
  // Free-form custom instructions appended to the AI's system prompt for
  // auto-replies. Persists in app_settings.ai_reply_instructions.
  const [aiInstructions, setAiInstructions] = useState("");
  const [docIds, setDocIds] = useState([]);
  const [initial, setInitial] = useState({
    enabled: false, template: DEFAULT_TEMPLATE, smartReply: false,
    aiInstructions: "", docIds: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Document library
  const [docs, setDocs] = useState([]);

  // Live preview controls
  const [previewName, setPreviewName] = useState("Mariama Diop");

  // Send-test controls
  const [testPhone, setTestPhone] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Per-project mode reads the merged "effective" settings (overrides +
    // global defaults). Global mode reads the singleton as before.
    const fetchSettings = projetId
      ? settingsApi.getForProject(projetId).then((r) => r.effective || {})
      : settingsApi.get();
    // Documents library is scoped: when editing a project we only show
    // global docs + docs that belong to this project.
    const fetchDocs = projetId
      ? documentsApi.list({ projetId, includeGlobal: true })
      : documentsApi.list();
    Promise.all([fetchSettings, fetchDocs])
      .then(([s, allDocs]) => {
        if (cancelled) return;
        const en = !!s.wa_welcome_enabled;
        const tpl = s.wa_welcome_template || DEFAULT_TEMPLATE;
        const sr = !!s.wa_smart_reply_enabled;
        const list = Array.isArray(allDocs) ? allDocs : [];
        let ids = Array.isArray(s.welcome_document_ids) ? s.welcome_document_ids : [];
        if (ids.length === 0 && !projetId) {
          // Legacy fallback only in GLOBAL mode — per-project mode is
          // explicit and a empty list means "no welcome doc on this project".
          ids = list.filter((d) => d.auto_send_on_new_lead).map((d) => d.id);
        }
        const ai = typeof s.ai_reply_instructions === "string" ? s.ai_reply_instructions : "";
        setEnabled(en);
        setTemplate(tpl);
        setSmartReply(sr);
        setAiInstructions(ai);
        setDocIds(ids);
        setDocs(list);
        setInitial({ enabled: en, template: tpl, smartReply: sr, aiInstructions: ai, docIds: ids });
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projetId]);

  const sameIds = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

  const dirty =
    enabled !== initial.enabled ||
    template !== initial.template ||
    smartReply !== initial.smartReply ||
    aiInstructions !== initial.aiInstructions ||
    !sameIds(docIds, initial.docIds);

  const selectedDocs = useMemo(() => {
    const byId = new Map(docs.map((d) => [d.id, d]));
    return docIds.map((id) => byId.get(id)).filter(Boolean);
  }, [docs, docIds]);

  const toggleDoc = (id) => {
    setDocIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const previewText = useMemo(
    () => renderTemplate(template, previewName),
    [template, previewName]
  );

  const save = async () => {
    if (enabled && !template.trim()) {
      toast.error("Le message ne peut pas être vide");
      return;
    }
    if (aiInstructions.length > AI_INSTRUCTIONS_MAX) {
      toast.error(`Les instructions IA dépassent ${AI_INSTRUCTIONS_MAX} caractères`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        wa_welcome_enabled: enabled,
        wa_welcome_template: template,
        wa_smart_reply_enabled: smartReply,
        ai_reply_instructions: aiInstructions,
        welcome_document_ids: docIds,
      };
      const res = projetId
        ? await settingsApi.updateForProject(projetId, payload)
        : await settingsApi.update(payload);
      // The per-project endpoint returns {effective, overrides}; the
      // global endpoint returns the flat singleton. Normalise to a single
      // shape so the rest of the function doesn't care.
      const s = projetId ? (res.effective || {}) : res;
      const newIds = Array.isArray(s.welcome_document_ids) ? s.welcome_document_ids : [];
      const newAi = typeof s.ai_reply_instructions === "string" ? s.ai_reply_instructions : "";
      setInitial({
        enabled: !!s.wa_welcome_enabled,
        template: s.wa_welcome_template || DEFAULT_TEMPLATE,
        smartReply: !!s.wa_smart_reply_enabled,
        aiInstructions: newAi,
        docIds: newIds,
      });
      toast.success(
        projetId
          ? `Paramètres enregistrés pour ${projetNom || "ce projet"}`
          : "Paramètres globaux enregistrés"
      );
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    const phone = testPhone.replace(/\s/g, "").trim();
    if (!phone) {
      toast.error("Indique un numéro de téléphone");
      return;
    }
    if (!template.trim()) {
      toast.error("Le message est vide");
      return;
    }
    if (dirty) {
      toast.error("Enregistre d'abord tes modifications avant de tester");
      return;
    }
    setSendingTest(true);
    try {
      const r = await welcomeApi.testSend({
        phone: testPhone.trim(),
        name: previewName,
        projetId,
      });
      const docCount = r?.documents_sent?.length || 0;
      const failed = r?.documents_failed || [];
      if (failed.length > 0) {
        toast.warning(
          `Texte envoyé. ${docCount} document(s) OK, ${failed.length} en échec : ${failed[0]?.error?.slice(0, 80) || ""}`
        );
      } else if (docCount > 0) {
        toast.success(
          `Test envoyé sur WhatsApp : texte + ${docCount} document${docCount > 1 ? "s" : ""}`
        );
      } else {
        toast.success("Test envoyé sur WhatsApp (texte seul, aucun document sélectionné)");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.humanMessage ||
        err?.message ||
        "Échec de l'envoi";
      toast.error(typeof msg === "string" ? msg : "Échec de l'envoi");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <section
      className="mt-6 rounded-2xl border border-border/60 bg-card p-5 shadow-soft sm:p-6"
      data-testid="welcome-autosend"
    >
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-base font-bold">Message de bienvenue automatique</h3>
          <p className="text-xs text-muted-foreground">
            Envoyé en WhatsApp à chaque nouveau prospect dès sa création.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
          disabled={loading}
          data-testid="welcome-toggle"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="welcome-template">Texte du message</Label>
        <Textarea
          id="welcome-template"
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          rows={5}
          placeholder={DEFAULT_TEMPLATE}
          disabled={loading || !enabled}
          data-testid="welcome-template"
        />
        <p className="text-xs text-muted-foreground">
          Variables disponibles : <code className="rounded bg-muted px-1">{"{nom}"}</code> (nom complet),{" "}
          <code className="rounded bg-muted px-1">{"{prenom}"}</code> (premier mot du nom).
        </p>
      </div>

      {/* ============== Live preview ============== */}
      <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/20">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Eye className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
          <span className="text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
            Aperçu pour
          </span>
          <Input
            value={previewName}
            onChange={(e) => setPreviewName(e.target.value)}
            placeholder="Nom du prospect"
            className="h-7 max-w-[180px] border-emerald-300 bg-white text-xs dark:border-emerald-800 dark:bg-zinc-900"
            data-testid="welcome-preview-name"
          />
        </div>
        <div className="flex items-start gap-2">
          <div className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full bg-emerald-600 text-white">
            <MessageCircle className="h-3.5 w-3.5" />
          </div>
          <div
            className="min-h-[40px] flex-1 whitespace-pre-wrap rounded-2xl rounded-tl-none bg-white px-3 py-2 text-sm leading-relaxed text-zinc-900 shadow-sm dark:bg-zinc-900 dark:text-zinc-100"
            data-testid="welcome-preview-rendered"
          >
            {previewText || (
              <span className="italic text-muted-foreground">
                (le message apparaîtra ici)
              </span>
            )}
          </div>
        </div>
        {selectedDocs.length > 0 && (
          <div className="mt-2 ml-9 space-y-1">
            {selectedDocs.map((d) => (
              <div
                key={d.id}
                className="flex items-center gap-2 rounded-md border border-emerald-300/60 bg-white/70 px-2 py-1.5 text-xs dark:border-emerald-800/40 dark:bg-zinc-900/70"
              >
                <Paperclip className="h-3 w-3 text-emerald-700 dark:text-emerald-300" />
                <FileText className="h-3.5 w-3.5 text-blue-600 dark:text-blue-300" />
                <span className="truncate font-medium">{d.nom}</span>
                <span className="ml-auto text-[10px] text-muted-foreground">
                  {Math.round((d.size || 0) / 1024)} KB
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="mt-2 text-[10px] text-muted-foreground">
          Aperçu en temps réel — c'est exactement ce qui partira sur WhatsApp
          {selectedDocs.length > 0 && ` (${selectedDocs.length} pièce${selectedDocs.length > 1 ? "s" : ""} jointe${selectedDocs.length > 1 ? "s" : ""})`}.
        </p>
      </div>

      {/* ============== Document picker ============== */}
      <div className="mt-4 rounded-xl border border-border/60 bg-card p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Files className="h-4 w-4 text-blue-600 dark:text-blue-300" />
          <h4 className="font-heading text-sm font-bold flex-1">
            Documents joints au message
          </h4>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold tabular-nums">
            {docIds.length} sélectionné{docIds.length > 1 ? "s" : ""}
          </span>
        </div>

        {docs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground">
            Aucun document dans la bibliothèque.{" "}
            <Link to="/parametres" className="font-semibold underline text-accent-navy">
              Téléverse-en un d'abord
            </Link>
            .
          </div>
        ) : (
          <div className="space-y-1">
            {docs.map((d) => {
              const checked = docIds.includes(d.id);
              return (
                <label
                  key={d.id}
                  className={`flex cursor-pointer items-center gap-2.5 rounded-md border px-2.5 py-2 text-sm transition ${
                    checked
                      ? "border-emerald-400 bg-emerald-50/60 dark:border-emerald-700 dark:bg-emerald-950/30"
                      : "border-border/60 hover:bg-muted/40"
                  }`}
                  data-testid={`welcome-doc-toggle-${d.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleDoc(d.id)}
                    className="h-4 w-4 cursor-pointer rounded border-border accent-emerald-600"
                  />
                  <FileText className="h-3.5 w-3.5 flex-none text-blue-600 dark:text-blue-300" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{d.nom}</div>
                    <div className="truncate text-[10px] text-muted-foreground">
                      {d.filename} · {Math.round((d.size || 0) / 1024)} KB
                      {d.caption && <> · « {d.caption.slice(0, 60)}{d.caption.length > 60 ? "…" : ""} »</>}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px]">
          <p className="text-muted-foreground">
            Coche les documents qui partent automatiquement avec le message de bienvenue.
            Décoche tout pour n'envoyer que le texte.
          </p>
          <Link
            to="/parametres"
            className="text-accent-navy underline hover:no-underline"
          >
            Gérer la bibliothèque →
          </Link>
        </div>
      </div>

      {/* ============== Save + Send test ============== */}
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-1 flex-col gap-1.5 sm:max-w-md">
          <Label htmlFor="welcome-test-phone" className="text-[11px]">
            Tester sur un numéro (envoi WhatsApp réel)
          </Label>
          <div className="flex gap-2">
            <Input
              id="welcome-test-phone"
              type="tel"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="+221 77 555 11 22"
              className="flex-1"
              data-testid="welcome-test-phone"
            />
            <Button
              variant="outline"
              onClick={sendTest}
              disabled={sendingTest || loading || !testPhone.trim()}
              className="gap-1.5"
              data-testid="welcome-test-send"
            >
              {sendingTest ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {sendingTest ? "Envoi…" : "Envoyer test"}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Le numéro doit être joignable sur WhatsApp. Idéal : ton propre numéro.
          </p>
        </div>

        <Button
          onClick={save}
          disabled={!dirty || saving || loading}
          className="gap-2"
          data-testid="welcome-save"
        >
          <Save className="h-4 w-4" />
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>

      {!enabled && (
        <p className="mt-3 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          ⚠️ Désactivé : aucun message n'est envoyé à la création d'un prospect.
          Active le switch en haut pour automatiser l'envoi.
        </p>
      )}

      {/* ============== Smart reply (inchangé) ============== */}
      <div className="mt-6 rounded-xl border border-border/60 bg-muted/30 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
            <Bot className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-heading text-sm font-bold">Réponse IA automatique aux formulaires</h4>
            <p className="text-xs text-muted-foreground">
              Quand un message WhatsApp contient les champs d'un formulaire (Meta Lead Ads, etc.),
              le CRM crée le lead automatiquement et répond avec un message adapté à l'intention d'achat.
            </p>
          </div>
          <Switch
            checked={smartReply}
            onCheckedChange={setSmartReply}
            disabled={loading}
            data-testid="smart-reply-toggle"
          />
        </div>
        {smartReply && (
          <>
            <p className="mt-3 text-xs text-muted-foreground">
              💡 Sans clé <code className="rounded bg-muted px-1">ANTHROPIC_API_KEY</code> dans
              backend/.env, une réponse de secours déterministe est utilisée.
            </p>

            {/* Custom instructions — appended to the AI's system prompt so
                the admin can steer tone, mention specific programmes, forbid
                topics, etc., without touching the code. */}
            <div className="mt-4 space-y-2">
              <Label
                htmlFor="ai-reply-instructions"
                className="flex items-center gap-1.5 text-xs"
              >
                <Sparkles className="h-3 w-3" />
                Instructions personnalisées pour l'IA
                <span className="text-[10px] font-normal text-muted-foreground">
                  (optionnel)
                </span>
              </Label>
              <Textarea
                id="ai-reply-instructions"
                value={aiInstructions}
                onChange={(e) => setAiInstructions(e.target.value)}
                rows={6}
                maxLength={AI_INSTRUCTIONS_MAX}
                placeholder={
                  "Ex :\n" +
                  "- Mentionne toujours que la livraison du programme RYAD Almadies est prévue T2 2027.\n" +
                  "- Ne donne JAMAIS de prix au m² — dis qu'un conseiller confirmera.\n" +
                  "- Si le lead vient de Mauritanie, propose la visio en priorité.\n" +
                  "- Tutoiement interdit — vouvoiement strict."
                }
                className="font-mono text-xs leading-relaxed"
                data-testid="ai-instructions-textarea"
              />
              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                <span>
                  Ces instructions sont injectées dans le prompt système à chaque
                  réponse automatique. Elles écrasent les règles par défaut en
                  cas de conflit.
                </span>
                <span className="tabular-nums">
                  {aiInstructions.length} / {AI_INSTRUCTIONS_MAX}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
